import React, { useEffect, useState } from 'react';
import { BrowserRouter, NavLink, Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import {
  FiBarChart2,
  FiBell,
  FiCalendar,
  FiClock,
  FiCpu,
  FiLifeBuoy,
  FiMapPin,
  FiMessageSquare,
  FiPhoneCall,
  FiTarget,
  FiUser,
  FiUsers,
} from 'react-icons/fi';
import api, { REQUIRED_API_VERSION, SALON_NAME } from './services/api';
import { nyafazEngagementFirstSegment, nyafazEngagementPath } from './nyafazEngagementPaths';
import Home from './pages/Home';
import Staff from './pages/Staff';
import Appointments from './pages/Appointments';
import Conversations from './pages/Conversations';
import HumanIntervention from './pages/HumanIntervention';
import Leads from './pages/Leads';
import Calls from './pages/Calls';
import Automation from './pages/Automation';
import RagUpload from './pages/RagUpload';
import VoiceCall from './pages/VoiceCall';
import './theme.css';

const NAV = [
  {
    section: 'Overview',
    items: [{ segment: '', icon: <FiBarChart2 />, label: 'Dashboard', element: <Home />, end: true }],
  },
  {
    section: 'Operations',
    items: [
      { segment: 'leads', icon: <FiTarget />, label: 'Leads', element: <Leads /> },
      { segment: 'appointments', icon: <FiCalendar />, label: 'Appointments', element: <Appointments />, badge: 'appointments_attention' },
      { segment: 'staff-availability', icon: <FiUsers />, label: 'Staff Availability', element: <Staff /> },
    ],
  },
  {
    section: 'AI Training',
    items: [{ segment: 'ai-docs', icon: <FiCpu />, label: 'WhatsApp AI Docs', element: <RagUpload /> }],
  },
  {
    section: 'Campaigns',
    items: [
      { segment: 'reminders', icon: <FiClock />, label: 'Reminders', element: <Automation /> },
      { segment: 'conversations', icon: <FiMessageSquare />, label: 'Conversations', element: <Conversations /> },
      { segment: 'human-intervention', icon: <FiLifeBuoy />, label: 'Human Intervention', element: <HumanIntervention />, badge: 'open_handoffs' },
      { segment: 'call-records', icon: <FiPhoneCall />, label: 'Call Records', element: <Calls /> },
    ],
  },
];

const NAV_ROUTES = NAV.flatMap((s) => s.items);
const BRAND_LOGO = `${process.env.PUBLIC_URL || ''}/nyafaz-engagement/nyafaz-logo.svg`;

const REDIRECTS = {
  staff: 'staff-availability',
  slots: 'staff-availability',
  'rag-upload': 'ai-docs',
  automation: 'reminders',
  calls: 'call-records',
};

const BADGE_POLL_MS = 15000;

const RESTART_COMMAND = ['cd nyufaz-analyze/backend', 'python naturals/app_engagement.py'].join('\n');

const MenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

function BackendBanner({ state }) {
  if (state === 'ok' || state === 'checking') return null;
  const outdated = state === 'outdated';
  return (
    <div className="nye-callout nye-callout--danger" style={{ marginBottom: 16 }}>
      <strong>{outdated ? 'The demo backend is running older code.' : 'The demo backend is not reachable.'}</strong>{' '}
      {outdated ? 'Some screens will not load until it is restarted.' : 'Start it, then reload this page.'}
      <pre style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap', fontSize: 12 }}>{RESTART_COMMAND}</pre>
    </div>
  );
}

function AdminShell({ children }) {
  const [counts, setCounts] = useState({});
  const [backend, setBackend] = useState('checking');
  // Below 900px the sidebar is a slide-in drawer opened from the top bar.
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && setNavOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  useEffect(() => {
    api
      .health()
      .then((h) => setBackend((h?.api_version || 0) >= REQUIRED_API_VERSION ? 'ok' : 'outdated'))
      .catch(() => setBackend('down'));
  }, []);

  useEffect(() => {
    document.title = `${SALON_NAME} — Customer Engagement`;
    let lastAlerts = null;
    const load = () =>
      api
        .summary()
        .then((d) => {
          const c = d.counts || {};
          if (lastAlerts !== null && (c.open_handoffs || 0) > lastAlerts) {
            toast('🆘 New human intervention request. Open Human Intervention to see it.', { duration: 6000 });
          }
          lastAlerts = c.open_handoffs || 0;
          setCounts(c);
        })
        .catch(() => {});
    load();
    const t = setInterval(load, BADGE_POLL_MS);
    return () => clearInterval(t);
  }, []);

  const main = children ?? <Outlet />;
  const currentPage = NAV_ROUTES.find((item) => item.segment && location.pathname.startsWith(nyafazEngagementPath(item.segment)));
  const alertCount = NAV_ROUTES.reduce((n, item) => n + (item.badge ? counts[item.badge] || 0 : 0), 0);

  return (
    <div className="nye-app">
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            fontFamily: "'Urbanist', sans-serif",
            fontSize: 13,
            background: '#ffffff',
            color: '#1f2d33',
            border: '1px solid rgba(29, 124, 119, 0.18)',
            boxShadow: '0 10px 30px rgba(18, 82, 78, 0.15)',
          },
          success: { iconTheme: { primary: '#1d7c77', secondary: '#ffffff' } },
          error: { iconTheme: { primary: '#c62828', secondary: '#ffffff' } },
        }}
      />

      <header className="nye-topbar">
        <button
          type="button"
          className="nye-topbar-menu"
          onClick={() => setNavOpen(true)}
          aria-label="Open menu"
          aria-expanded={navOpen}
          aria-controls="nye-sidebar"
        >
          <MenuIcon />
          {alertCount > 0 ? <span className="nye-topbar-dot" aria-hidden /> : null}
        </button>
        <div className="nye-topbar-title nye-ellipsis">{currentPage?.label || 'Dashboard'}</div>
        <img className="nye-topbar-logo" src={BRAND_LOGO} alt={SALON_NAME} />
      </header>
      <div className={`nye-nav-backdrop${navOpen ? ' is-open' : ''}`} onClick={() => setNavOpen(false)} aria-hidden />

      <aside id="nye-sidebar" className={`nye-sidebar${navOpen ? ' is-open' : ''}`}>
        <div className="nye-brand">
          <button type="button" className="nye-sidebar-close" onClick={() => setNavOpen(false)} aria-label="Close menu">
            ✕
          </button>
          <img className="nye-brand-logo" src={BRAND_LOGO} alt={SALON_NAME} />
          <div className="nye-brand-tag">Customer Engagement</div>
        </div>

        <nav className="nye-nav">
          {NAV.map((section) => (
            <div key={section.section}>
              <div className="nye-nav-section">{section.section}</div>
              {section.items.map((item) => (
                <NavLink
                  key={item.segment || 'home'}
                  to={nyafazEngagementPath(item.segment)}
                  end={item.end}
                  onClick={() => setNavOpen(false)}
                  title={item.label}
                  aria-label={item.label}
                  className={({ isActive }) => `nye-nav-item${isActive ? ' active' : ''}`}
                >
                  <span className="nye-nav-icon">{item.icon}</span>
                  <span className="nye-nav-label">{item.label}</span>
                  {item.badge && counts[item.badge] > 0 ? <span className="nye-nav-badge">{counts[item.badge]}</span> : null}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="nye-user-card">
          <div className="nye-avatar">{SALON_NAME.slice(0, 1).toUpperCase()}</div>
          <div style={{ overflow: 'hidden' }}>
            <div className="nye-user-name nye-ellipsis">{SALON_NAME}</div>
            <div className="nye-user-role">Salon admin · demo</div>
          </div>
        </div>
      </aside>

      <div className="nye-content">
        <main className="nye-main">
          {/* Desktop header bar: scrolls with the page (phones use .nye-topbar above) */}
          <header className="nye-header">
            <div className="nye-header-salon">
              <FiMapPin className="nye-header-salon-icon" aria-hidden />
              <div>
                <div className="nye-header-salon-name">{SALON_NAME}</div>
                <div className="nye-header-salon-sub">Salon admin · demo</div>
              </div>
            </div>
            <div className="nye-header-actions">
              <NavLink to={nyafazEngagementPath('conversations')} className="nye-header-icon" title="Conversations" aria-label="Conversations">
                <FiMessageSquare />
              </NavLink>
              <NavLink
                to={nyafazEngagementPath('human-intervention')}
                className="nye-header-icon"
                title="Human intervention requests"
                aria-label="Human intervention requests"
              >
                <FiBell />
                {counts.open_handoffs > 0 ? <span className="nye-header-badge">{counts.open_handoffs}</span> : null}
              </NavLink>
              <span className="nye-header-divider" aria-hidden />
              <div className="nye-header-user">
                <span className="nye-header-avatar">
                  <FiUser />
                </span>
                <div>
                  <div className="nye-header-hi">Hi {SALON_NAME}</div>
                  <div className="nye-header-date">{new Date().toLocaleDateString('en-GB')}</div>
                </div>
              </div>
            </div>
          </header>
          <div className="nye-main-inner">
            <BackendBanner state={backend} />
            {main}
          </div>
        </main>
      </div>
    </div>
  );
}

function NyafazEngagementEmbedded() {
  const location = useLocation();
  const splatParam = useParams()['*'];
  let splat = splatParam ?? '';
  if (!splat && location.pathname.startsWith('/nyafaz-ep/nyafaz-engagement')) {
    splat = location.pathname.slice('/nyafaz-ep/nyafaz-engagement'.length).replace(/^\//, '');
  }
  const segment = nyafazEngagementFirstSegment(splat);

  if (segment === 'voice-call') {
    return <VoiceCall />;
  }

  const redirectTo = REDIRECTS[segment];
  if (redirectTo) {
    return <Navigate to={nyafazEngagementPath(redirectTo)} replace />;
  }

  if (segment && !NAV_ROUTES.some((item) => item.segment === segment)) {
    return <Navigate to={nyafazEngagementPath()} replace />;
  }

  const page = NAV_ROUTES.find((item) => item.segment === segment) || NAV_ROUTES.find((item) => item.segment === '');
  return <AdminShell>{page?.element}</AdminShell>;
}

export function NyafazEngagementRoutes() {
  return (
    <Routes>
      <Route path="voice-call" element={<VoiceCall />} />
      <Route element={<AdminShell />}>
        {NAV_ROUTES.map((item) =>
          item.segment === '' ? (
            <Route key="home" index element={item.element} />
          ) : (
            <Route key={item.segment} path={item.segment} element={item.element} />
          ),
        )}
        {Object.entries(REDIRECTS).map(([from, to]) => (
          <Route key={from} path={from} element={<Navigate to={to} replace />} />
        ))}
        <Route path="*" element={<Navigate to="." replace />} />
      </Route>
    </Routes>
  );
}

/** Standalone `npm run start:nyafaz-engagement` wraps routes in BrowserRouter; main App.js does not. */
export default function NyafazEngagementApp() {
  if (process.env.REACT_APP_TARGET === 'nyafaz-engagement') {
    return (
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <NyafazEngagementRoutes />
      </BrowserRouter>
    );
  }
  return <NyafazEngagementEmbedded />;
}
